import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { logError } from "@faire/web/common/logging";
import { useState } from "@faire/web/ui/hooks/useState";
import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import styled, { css } from "styled-components";

import { accordionTransitionTiming } from "./Accordion";
import { AccordionContext } from "./context";

interface IProps extends PropsWithChildren {
  /**
   * Should click events be propagated to the accordion component. If falsy then click events on
   * children will cause the accordion to close.
   */
  stopPropagation?: boolean;
}

/**
 * A component that only visually shows its children when its enclosing accordion is open.
 */
export const ExpandedContent: React.FC<IProps> = ({
  children,
  stopPropagation,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const context = useContext(AccordionContext);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (context === null) {
      logError("Trying to use AccordionContext outside of a provider");
    }
  }, [context]);

  useLayoutEffect(() => {
    const computeHeight = debounceIfPossible(() => {
      setHeight(ref.current?.clientHeight ?? 0);
    });

    computeHeight();

    getWindow()?.addEventListener("resize", computeHeight);
    return () => {
      getWindow()?.removeEventListener("resize", computeHeight);
    };
  }, [children]);

  return (
    <Wrapper
      $isOpen={!!context}
      $maxHeight={height}
      onClick={stopPropagation ? onClick : undefined}
      $stopPropagation={stopPropagation}
    >
      <div ref={ref}>{children}</div>
    </Wrapper>
  );
};

const onClick = (e: React.MouseEvent) => {
  e.stopPropagation();
};

interface IWrapperProps {
  $isOpen: boolean;
  $maxHeight?: number | null;
  $stopPropagation?: boolean;
}

const Wrapper = styled.div<IWrapperProps>`
  max-height: ${({ $isOpen, $maxHeight }) =>
    // eslint-disable-next-line no-nested-ternary
    $isOpen
      ? $maxHeight
        ? css`
            ${$maxHeight}px
          `
        : "initial"
      : "0"};
  height: 100%;
  overflow: hidden;
  @media (prefers-reduced-motion: no-preference) {
    transition-property: max-height;
    ${accordionTransitionTiming}
  }
  cursor: ${({ $stopPropagation }) => ($stopPropagation ? "initial" : "unset")};
`;
